export const ALL_PRODUCT_CATEGORIES = "all";

// Keep sold-out assigned products visible, but never mix the outlet catalog into a seller's POS.
export const filterSellerCatalog = (products = []) => products.flatMap((product) => {
  if (Array.isArray(product?.variants)) {
    const variants = filterSellerCatalog(product.variants);
    return variants.length ? [{ ...product, variants }] : [];
  }
  return product?.stock_source !== "seller" || product?.seller_stock_assigned ? [product] : [];
});

export const getProductCategoryKey = (product = {}) =>
  String(product?.category || product?.category_label || "")
    .trim()
    .toLowerCase();

export const buildProductCategoryOptions = (products = []) => {
  const categories = new Map();

  products.forEach((product) => {
    const value = getProductCategoryKey(product);
    const label = String(product?.category_label || product?.category || "").trim();

    if (value && label && !categories.has(value)) {
      categories.set(value, { value, label });
    }
  });

  return Array.from(categories.values()).sort((left, right) => left.label.localeCompare(right.label, "id"));
};

export const getProductStockLimit = (product = {}) => {
  if (product?.is_unlimited || product?.inventory_mode === "non_stock") {
    return Number.POSITIVE_INFINITY;
  }

  if (product?.inventory_mode === "made_to_order") {
    if (product?.available_qty !== null && product?.available_qty !== undefined) {
      return Math.max(0, Number(product.available_qty || 0));
    }

    // Made-to-order tidak pernah dibatasi oleh stok produk jadi. Pada payload lama
    // yang belum mempunyai available_qty, ikuti status availability API bila ada.
    return product?.can_sell === false ? 0 : Number.POSITIVE_INFINITY;
  }

  if (product?.available_qty !== null && product?.available_qty !== undefined) {
    return Math.max(0, Number(product.available_qty || 0));
  }

  return Math.max(0, Number(product?.stock || 0));
};

export const getProductRemainingStock = (product = {}, details = []) => {
  const stock = getProductStockLimit(product);
  const quantityInCart = details
    .filter((detail) => String(detail?.product_id) === String(product?.id))
    .reduce((total, detail) => total + Number(detail?.qty || 0), 0);

  return Number.isFinite(stock) ? Math.max(0, stock - quantityInCart) : Number.POSITIVE_INFINITY;
};

const isMadeToOrderUnavailable = (product = {}) => {
  if (product?.inventory_mode !== "made_to_order") return false;

  const hasServerCapacity = product?.available_qty !== null && product?.available_qty !== undefined;

  return product?.can_sell === false || (hasServerCapacity && Number(product.available_qty || 0) <= 0);
};

export const getCatalogAvailabilityTarget = (catalogItem = {}) => {
  const variants = catalogItem?.is_product_group ? catalogItem?.variants || [] : [];
  const product = variants.length === 1 ? variants[0] : catalogItem;

  if (catalogItem?.is_product_group && variants.length !== 1) return null;
  if (!isMadeToOrderUnavailable(product)) return null;

  return {
    ...product,
    name: catalogItem?.is_product_group ? catalogItem?.name : product?.name,
    product_group_name: product?.product_group_name || (catalogItem?.is_product_group ? catalogItem?.name : null),
  };
};

export const canOpenCatalogProduct = (catalogItem = {}) =>
  Boolean(catalogItem?.is_product_group && Number(catalogItem?.variant_count || 0) > 1) ||
  Boolean(getCatalogAvailabilityTarget(catalogItem));

export const buildVisibleProducts = (products = [], details = [], selectedCategory = ALL_PRODUCT_CATEGORIES) =>
  products
    .filter(
      (product) =>
        selectedCategory === ALL_PRODUCT_CATEGORIES || getProductCategoryKey(product) === selectedCategory,
    )
    .map((product) => ({
      ...product,
      remaining_stock: getProductRemainingStock(product, details),
    }));

export const buildVisibleProductGroups = (
  groups = [],
  details = [],
  selectedCategory = ALL_PRODUCT_CATEGORIES,
) =>
  groups
    .filter(
      (group) => selectedCategory === ALL_PRODUCT_CATEGORIES || getProductCategoryKey(group) === selectedCategory,
    )
    .map((group) => {
      const variants = (group?.variants || []).map((variant) => ({
        ...variant,
        remaining_stock: getProductRemainingStock(variant, details),
      }));
      const sellableVariants = variants.filter(
        (variant) => variant?.can_sell !== false && variant.remaining_stock > 0,
      );
      const prices = variants.map((variant) => Number(variant?.unit_price || 0));

      return {
        ...group,
        is_product_group: true,
        variants,
        variant_count: variants.length,
        can_sell: sellableVariants.length > 0,
        unit_price: prices.length ? Math.min(...prices) : 0,
        minimum_price: prices.length ? Math.min(...prices) : 0,
        maximum_price: prices.length ? Math.max(...prices) : 0,
      };
    });
